import { useState, useEffect } from 'react';
import { collection, query, where, orderBy } from 'firebase/firestore';
import { monitoredOnSnapshot } from '../utils/firestoreMonitor';
import { db } from '../firebase/firebaseConfig';
import { Redemption } from '../types/rewards';
import { useAuth } from '../context/AuthContext';

/**
 * Hook para obtener canjes pendientes de una familia
 */
export function usePendingRedemptions(familyId: string | null) {
  const { user } = useAuth();
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // No ejecutar query si no hay usuario autenticado
    if (!user || !familyId) {
      setRedemptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'redemptions'),
      where('familyId', '==', familyId),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = monitoredOnSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d: any) => ({
          id: d.id,
          ...d.data()
        })) as Redemption[];
        
        setRedemptions(list);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading pending redemptions:', err);
        setError(err.message);
        setLoading(false);
      },
      { component: 'usePendingRedemptions', query: 'redemptions pendientes' }
    );

    return () => unsubscribe();
  }, [familyId, user?.uid]);

  return { redemptions, loading, error, count: redemptions.length };
}

/**
 * Hook para obtener historial de canjes de un miembro
 */
export function useMemberRedemptions(memberId: string | null, familyId: string | null) {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!memberId || !familyId) {
      setRedemptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'redemptions'),
      where('memberId', '==', memberId),
      where('familyId', '==', familyId),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = monitoredOnSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d: any) => ({
          id: d.id,
          ...d.data()
        })) as Redemption[];
        
        setRedemptions(list);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading member redemptions:', err);
        setError(err.message);
        setLoading(false);
      },
      { component: 'useMemberRedemptions', query: 'redemptions del miembro' }
    );

    return () => unsubscribe();
  }, [memberId, familyId]);

  return { redemptions, loading, error };
}

/**
 * Hook para obtener todos los canjes de una familia (admin)
 */
export function useFamilyRedemptions(familyId: string | null) {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!familyId) {
      setRedemptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'redemptions'),
      where('familyId', '==', familyId),
      orderBy('requestedAt', 'desc')
    );

    const unsubscribe = monitoredOnSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d: any) => ({
          id: d.id,
          ...d.data()
        })) as Redemption[];
        
        setRedemptions(list);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading family redemptions:', err);
        setError(err.message);
        setLoading(false);
      },
      { component: 'useFamilyRedemptions', query: 'todos los redemptions de la familia' }
    );

    return () => unsubscribe();
  }, [familyId]);

  return { redemptions, loading, error };
}
